import { clsx } from 'clsx';

/**
 * Combina classes CSS usando clsx
 * @param {...any} classes - Classes CSS para combinar
 * @returns {string} - String de classes combinadas
 */
export const cn = (...classes) => clsx(classes);

/**
 * Arredonda um valor monetário para 2 casas decimais (padrão BRL)
 * @param {number} value - Valor a ser arredondado
 * @returns {number} - Valor arredondado (ex: 10.505 → 10.51)
 */
export const roundMoney = value => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

/**
 * Formata um valor monetário para o padrão brasileiro
 * @param {number} value - Valor a ser formatado
 * @returns {string} - Valor formatado (ex: R$ 10,50)
 */
export const formatCurrency = value => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(roundMoney(value));
};

/**
 * Interpreta valor de data para exibição. Strings só com YYYY-MM-DD são tratadas como
 * data de calendário no fuso local (evita mostrar 1 dia a menos em UTC-3 e similares).
 * @param {Date|string|number|null|undefined} input
 * @returns {Date|null}
 */
export const parseDateForDisplay = input => {
  if (input == null || input === '') return null;
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input;
  }
  const s = String(input).trim();
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (ymd) {
    const y = Number(ymd[1]);
    const m = Number(ymd[2]) - 1;
    const d = Number(ymd[3]);
    const local = new Date(y, m, d);
    return isNaN(local.getTime()) ? null : local;
  }
  const t = new Date(s);
  return isNaN(t.getTime()) ? null : t;
};

/**
 * Formata uma data para o padrão brasileiro
 * @param {Date|string} date - Data a ser formatada
 * @returns {string} - Data formatada (ex: 15/12/2023)
 */
export const formatDate = date => {
  const d = parseDateForDisplay(date);
  if (!d) return '';
  return new Intl.DateTimeFormat('pt-BR').format(d);
};

/**
 * Formata uma data e hora para o padrão brasileiro
 * @param {Date|string} date - Data a ser formatada
 * @returns {string} - Data e hora formatadas (ex: 15/12/2023 às 14:30)
 */
export const formatDateTime = date => {
  const d = parseDateForDisplay(date);
  if (!d) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

/**
 * Gera um ID único
 * @returns {string} - ID único
 */
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Valida se um email é válido
 * @param {string} email - Email a ser validado
 * @returns {boolean} - True se válido
 */
export const isValidEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valida se um CPF é válido
 * @param {string} cpf - CPF a ser validado
 * @returns {boolean} - True se válido
 */
export const isValidCPF = cpf => {
  cpf = cpf.replace(/[^\d]/g, '');

  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(10))) return false;

  return true;
};

/**
 * Debounce de função
 * @param {Function} func - Função a ser debounced
 * @param {number} wait - Tempo de espera em ms
 * @returns {Function} - Função debounced
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Texto de sabor/descrição curta vindo da API de produtos (campos variam por endpoint).
 * @param {Record<string, unknown>|null|undefined} p - Objeto produto ou linha da API
 * @returns {string}
 */
export const flavorDescriptionFromApi = p => {
  if (!p || typeof p !== 'object') return '';
  const raw =
    p.Sabor ??
    p.sabor ??
    p.descricao_sabor ??
    p.Descricao_Sabor ??
    p.descricao ??
    p.Descricao ??
    '';
  const s = String(raw).trim();
  return s;
};
